import type { GridRowSelectionModel } from '@mui/x-data-grid';
import {
  parseSurveyTeamScheduledDates,
  type StrapiSurveyGridRow,
  type SurveyRowStatus,
} from '../../api/strapiCrm';

export type SurveyRowPatch = Partial<
  Pick<
    StrapiSurveyGridRow,
    | 'berita_acara_document_id'
    | 'status'
    | 'start_time'
    | 'end_time'
    | 'cancelled_at'
    | 'cancel_reason'
    | 'customer_approved_sign'
    | 'surveyor_sign'
    | 'reschedule_status'
    | 'reschedule_date'
    | 'reschedule_schedule_document_id'
    | 'scheduled_date'
    | 'list_order'
  >
>;

export function surveyRowStatusFromSigns(opts: {
  end_time?: string | null;
  customer_approved_sign?: string | null;
  surveyor_sign?: string | null;
  cancelled_at?: string | null;
}): SurveyRowStatus {
  if (String(opts.cancelled_at ?? '').trim()) return 'cancelled';
  const cust = String(opts.customer_approved_sign ?? '').trim();
  const surv = String(opts.surveyor_sign ?? '').trim();
  if (cust && surv) return 'completed';
  if (cust) return 'customer_signed';
  if (opts.end_time) return 'finished';
  return 'in_progress';
}

function rowEligibleForSign(
  row: StrapiSurveyGridRow,
  mode: 'customer' | 'surveyor',
): boolean {
  if (!row.berita_acara_document_id) return false;
  if (mode === 'customer') return row.status === 'finished';
  return (
    (row.status === 'finished' || row.status === 'customer_signed') &&
    !String(row.surveyor_sign ?? '').trim()
  );
}

export function selectedSignRows(
  rows: StrapiSurveyGridRow[],
  selection: GridRowSelectionModel,
  mode: 'customer' | 'surveyor',
): StrapiSurveyGridRow[] {
  const keys = new Set(selection.map(String));
  return rows.filter((row) => keys.has(row.row_key) && rowEligibleForSign(row, mode));
}

export function selectedSignDocIds(
  rows: StrapiSurveyGridRow[],
  selection: GridRowSelectionModel,
  mode: 'customer' | 'surveyor',
): string[] {
  return selectedSignRows(rows, selection, mode)
    .map((row) => row.berita_acara_document_id!)
    .filter(Boolean);
}

export function patchSurveyRowAfterSign(
  row: StrapiSurveyGridRow,
  kind: 'customer' | 'surveyor',
  sign: string,
): SurveyRowPatch {
  const trimmed = sign.trim();
  const customer_approved_sign = kind === 'customer' ? trimmed : row.customer_approved_sign;
  const surveyor_sign = kind === 'surveyor' ? trimmed : row.surveyor_sign;
  return {
    customer_approved_sign,
    surveyor_sign,
    status: surveyRowStatusFromSigns({
      end_time: row.end_time,
      customer_approved_sign,
      surveyor_sign,
    }),
  };
}

export function surveyRowHasStarted(row: StrapiSurveyGridRow): boolean {
  if (row.status === 'cancelled') return true;
  if (row.berita_acara_document_id) return true;
  return row.status !== 'not_started';
}

export function canStartSurveyRow(row: StrapiSurveyGridRow): boolean {
  if (row.status === 'cancelled') return false;
  if (row.reschedule_status === 'pending_staff') return false;
  if (!String(row.job_number ?? '').trim()) return false;
  return !surveyRowHasStarted(row);
}

/** Batalkan titik yang belum dimulai (tidak bisa undo). */
export function canCancelSurveyRow(row: StrapiSurveyGridRow): boolean {
  if (row.status !== 'not_started' || row.berita_acara_document_id) return false;
  // Titik dengan reschedule aktif dikelola lewat jadwal reschedule, bukan dibatalkan di sini.
  if (row.reschedule_status === 'pending_staff' || row.reschedule_status === 'ready') return false;
  return true;
}

/** Reschedule titik yang belum dimulai dan belum punya jadwal reschedule aktif. */
export function canRescheduleSurveyRow(row: StrapiSurveyGridRow): boolean {
  if (row.status !== 'not_started' || row.berita_acara_document_id) return false;
  if (row.reschedule_status === 'pending_staff' || row.reschedule_status === 'ready') return false;
  return true;
}

/**
 * Tanggal yang tidak boleh dipilih saat ajukan reschedule untuk WO yang sama:
 * slot tim survey + tanggal terjadwal / reschedule pada baris BA.
 */
export function collectBlockedRescheduleDates(options: {
  workOrderDocumentId: string;
  rows: StrapiSurveyGridRow[];
  /** Raw `scheduled_date` JSON arrays from survey-team rows for this WO. */
  teamScheduledDateFields?: unknown[];
}): string[] {
  const woId = options.workOrderDocumentId.trim();
  if (!woId) return [];
  const out = new Set<string>();

  for (const raw of options.teamScheduledDateFields ?? []) {
    for (const date of parseSurveyTeamScheduledDates(raw)) out.add(date);
  }

  for (const row of options.rows) {
    if (String(row.work_order_document_id ?? '').trim() !== woId) continue;
    const scheduled = String(row.scheduled_date ?? '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(scheduled)) out.add(scheduled);
    const reschedule = String(row.reschedule_date ?? '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(reschedule)) out.add(reschedule);
  }

  return [...out].sort();
}

/** Titik siap dicetak: TTD petugas dan pelanggan sudah ada. */
export function surveyRowIsBeritaAcaraPrintReady(row: StrapiSurveyGridRow): boolean {
  if (row.status === 'cancelled') return false;
  return Boolean(String(row.customer_approved_sign ?? '').trim() && String(row.surveyor_sign ?? '').trim());
}

export function canFinishSurveyRow(row: StrapiSurveyGridRow): boolean {
  if (row.status === 'cancelled') return false;
  if (row.end_time) return false;
  if (
    row.status === 'finished' ||
    row.status === 'customer_signed' ||
    row.status === 'completed'
  ) {
    return false;
  }
  return surveyRowHasStarted(row);
}

/** Titik sudah selesai / TTD: buka ulang form sampling untuk mengedit data. */
export function canEditSurveySamplingRow(row: StrapiSurveyGridRow): boolean {
  if (row.status === 'cancelled') return false;
  if (!row.berita_acara_document_id) return false;
  return (
    row.status === 'finished' ||
    row.status === 'customer_signed' ||
    row.status === 'completed' ||
    Boolean(row.end_time)
  );
}

export function applySurveyRowPatch(
  row: StrapiSurveyGridRow,
  patch: SurveyRowPatch | undefined,
): StrapiSurveyGridRow {
  if (!patch) return row;
  return {
    ...row,
    ...patch,
    berita_acara_document_id:
      patch.berita_acara_document_id ?? row.berita_acara_document_id,
    status: patch.status ?? row.status,
    start_time: patch.start_time ?? row.start_time,
    end_time: patch.end_time ?? row.end_time,
    cancelled_at: patch.cancelled_at ?? row.cancelled_at,
    cancel_reason: patch.cancel_reason ?? row.cancel_reason,
    customer_approved_sign: patch.customer_approved_sign ?? row.customer_approved_sign,
    surveyor_sign: patch.surveyor_sign ?? row.surveyor_sign,
    reschedule_status: patch.reschedule_status ?? row.reschedule_status,
    reschedule_date: patch.reschedule_date ?? row.reschedule_date,
    reschedule_schedule_document_id:
      patch.reschedule_schedule_document_id ?? row.reschedule_schedule_document_id,
    scheduled_date: patch.scheduled_date ?? row.scheduled_date,
    list_order: patch.list_order !== undefined ? patch.list_order : row.list_order,
  };
}

export function mergeSurveyRowsWithPatches(
  rows: StrapiSurveyGridRow[],
  patches: Record<string, SurveyRowPatch>,
): StrapiSurveyGridRow[] {
  return rows.map((row) => applySurveyRowPatch(row, patches[row.row_key]));
}
